import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { timestampToDate } from 'src/utils/dateFormatter';
import { decamelize } from 'src/utils/decamelize';

import { normalize } from '../shared/normalizers';
import { User } from '../user/_types';

import {
  Group,
  GroupCalendar,
  GroupMembership,
  GroupSearchResult,
} from './_types';
import {
  denormalizeGroup,
  normalizeGroupSearchResult,
  normalizeMyGroups,
} from './normalizers';

export const fetchGroups = async ({
  sortBy,
  sortDir,
  page = 0,
  perPage = 20,
  search = '',
  isPublic = false,
}: {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  search?: string;
  isPublic?: boolean;
}): Promise<{ groups: Group[]; total: number }> => {
  try {
    const response = await api.get('/api/v2/groups', {
      params: {
        page,
        per: perPage,
        ...(search && { 'q[name_cont]': search }),
        ...(sortBy && {
          'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
        }),
        ...(isPublic && { public: true }),
      },
    });
    const normalizedGroups = response.data?.group?.map((group) =>
      normalize(group)
    );

    return { groups: normalizedGroups, total: response.data?.meta?.total };
  } catch (error) {
    throw new Error(api.formatError(error, 'Error fetching groups.'));
  }
};
export const saveGroup = async ({ ...group }: Group): Promise<Group> => {
  const data = denormalizeGroup(group);
  const response = group.id
    ? await api.put(`/api/v3/groups/${group.id}`, { group: data })
    : await api.post('/api/v3/groups', { group: data });
  return normalize(response.data);
};

export const handleFileUpload = async (
  file: File
): Promise<{ id: string; url: string }> => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);
  const response = await api.post('/api/v2/assets/image/group', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const fetchClientGroups = async (
  userId: User['id']
): Promise<GroupMembership[]> => {
  const response = await api.get('/api/v3/group_members', {
    params: {
      user_id: userId,
      fetch_all: true,
    },
  });

  return response.data.map((membership) => ({
    ...(normalize(membership) as GroupMembership),
    createdAt: timestampToDate(membership.created_at),
    updatedAt: timestampToDate(membership.updated_at),
  }));
};

export const fetchAllGroups = async (platformId?: string): Promise<Group[]> => {
  const response = await api.get('/api/v2/groups', {
    params: {
      fetch_all: true,
      with_stripe: true,
      'q[s]': 'name asc',
      ...(platformId && { platform_id: platformId }),
    },
  });

  return response.data.map(normalize);
};

export const fetchAllGroupsByPluckColumns = async (
  pluckColumns: string,
  platformId?: string
): Promise<Group[]> => {
  const response = await api.get('/api/v2/groups', {
    params: {
      fetch_all: true,
      pluck_columns: pluckColumns,
      'q[s]': 'name asc',
      ...(platformId && { platform_id: platformId }),
    },
  });

  return response.data.map(normalize);
};

interface FetchGroupOptions {
  id: Group['id'];
}

export const fetchGroup = async ({ id }: FetchGroupOptions): Promise<Group> => {
  const response = await api.get(`/api/v3/groups/${id}`);
  return normalize(response.data);
};

export const fetchGroupsByParams = async (params = {}): Promise<Group[]> => {
  const response = await api.get(`/api/v3/groups`, {
    params: { ...params },
  });
  return response.data.map(normalize);
};

export const fetchGroupCalendars = async (): Promise<GroupCalendar[]> => {
  const response = await api.get('/api/v3/groups/calendars');
  return response.data.map(normalize);
};

export const removeFromGroup = async (
  membershipId: GroupMembership['id']
): Promise<void> => {
  await api.delete(`/api/v3/group_members/${membershipId}`);
};

export const addToGroup = async (
  userId: User['id'],
  groupId: Group['id']
): Promise<GroupMembership> => {
  const response = await api.post('/api/v3/group_members', {
    user_id: userId,
    group_id: groupId,
  });

  return normalize(response.data);
};

export const cloneGroup = async (groupId: Group['id']): Promise<Group> => {
  return await api.get(`/api/v2/groups/${groupId}/clone`);
};

export const deleteGroup = async (groupId: Group['id']): Promise<void> => {
  return await api.delete(`/api/v3/groups/${groupId}`);
};
export const fetchUserGroups = async (params: {
  for_user?: boolean;
  member_only?: boolean;
  ids?: number[];
}): Promise<Group[]> => {
  try {
    const response = await api.get('/api/v2/groups', {
      params: {
        fetch_all: true,
        ...params,
      },
    });
    return response.data.map(normalizeMyGroups);
  } catch (error) {
    throw new Error(api.formatError(error, 'Error fetching user groups.'));
  }
};

export const searchGroupResults = async ({
  search,
  trainerId,
  signal,
}: {
  search: string;
  trainerId: Identifier;
  signal?: AbortSignal;
}): Promise<GroupSearchResult[]> => {
  const response = await api.get('/sm/search', {
    signal,
    params: {
      types: [`group_white_label_${trainerId}`],
      limit: 20,
      no_limit: false,
      term: search,
      page: 1,
    },
  });
  return response.data.results[`group_white_label_${trainerId}`].map(
    (r: GroupSearchResult) => normalizeGroupSearchResult(r)
  );
};
